import { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { extractUsedColors } from "../../../../utils/recipeColor.utill";
import {
  buildDynamicColumns,
  buildDynamicColumnsWithRealTimeUpdates,
  buildFixedColumnsWithRealTimeUpdates,
  buildFormDefaultValues,
  buildIngredientNames,
  computeRecipeTotals
} from "../recipeVariant.utils";

type UseManageRecipeVariantFormParams = {
  recipeData: any;
  variants: any[];
  staticRecipeIngredientList: any[];
};

export const useManageRecipeVariantForm = ({
  recipeData,
  variants,
  staticRecipeIngredientList
}: UseManageRecipeVariantFormParams) => {
  const usedColors = useMemo(() => extractUsedColors(recipeData ?? null), [recipeData]);
  const dynamicColumns = useMemo(() => buildDynamicColumns(variants), [variants]);

  const ingredientNames = useMemo(
    () => buildIngredientNames(recipeData, staticRecipeIngredientList),
    [recipeData, staticRecipeIngredientList]
  );

  const generateDefaultValues = useMemo(
    () =>
      buildFormDefaultValues(
        recipeData,
        ingredientNames,
        recipeData?.variants || [],
        staticRecipeIngredientList
      ),
    [recipeData, ingredientNames, staticRecipeIngredientList]
  );

  const generateDefaultValuesRef = useRef(generateDefaultValues);
  generateDefaultValuesRef.current = generateDefaultValues;

  const formSyncKey = useMemo(
    () =>
      recipeData?._id
        ? `${recipeData._id}:${variants.map(variant => variant._id).join(",")}:${ingredientNames.map(ing => ing.ingredientId).join(",")}`
        : "",
    [recipeData?._id, variants, ingredientNames]
  );

  const {
    formState: { errors },
    control,
    watch,
    handleSubmit,
    reset
  } = useForm({
    defaultValues: generateDefaultValues
  });

  useEffect(() => {
    if (!formSyncKey) {
      return;
    }

    reset(generateDefaultValuesRef.current);
  }, [formSyncKey, reset]);

  const values = watch();

  const fixedColumnsWithRealTimeUpdates = useMemo(
    () => buildFixedColumnsWithRealTimeUpdates(recipeData, values),
    [values, recipeData]
  );

  const dynamicColumnsWithRealTimeUpdates = useMemo(
    () =>
      buildDynamicColumnsWithRealTimeUpdates(
        dynamicColumns,
        variants,
        values,
        recipeData?.originalRecipe
      ),
    [dynamicColumns, variants, values, recipeData?.originalRecipe]
  );

  const hasUpdatedColumns = useCallback(
    (columns: any[]) => columns.some(col => col?.hasUpdatedIngredients),
    []
  );

  const hasAnyUpdates = useMemo(
    () =>
      hasUpdatedColumns(fixedColumnsWithRealTimeUpdates) ||
      hasUpdatedColumns(dynamicColumnsWithRealTimeUpdates),
    [fixedColumnsWithRealTimeUpdates, dynamicColumnsWithRealTimeUpdates, hasUpdatedColumns]
  );

  const totals = useMemo(
    () =>
      computeRecipeTotals(
        values,
        dynamicColumnsWithRealTimeUpdates,
        ingredientNames,
        variants
      ),
    [values, dynamicColumnsWithRealTimeUpdates, ingredientNames, variants]
  );

  return {
    usedColors,
    ingredientNames,
    control,
    errors,
    handleSubmit,
    totals,
    fixedColumnsWithRealTimeUpdates,
    dynamicColumnsWithRealTimeUpdates,
    hasAnyUpdates
  };
};
